import { RootState, store } from 'modules/Editor/store';
import { selectZoom } from 'modules/Editor/store/editorSettingsSelector';
import { selectInfographHeightPx, selectInfographWidthPx, selectWidgets } from 'modules/Editor/store/infographSelector';
import { ActiveWidget } from 'modules/Editor/store/widgetControlSlice';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { GroupWidgetData, WidgetMap } from 'widgets/Widget.types';

import { Boundary } from '../AlignmentMenu.types';

/**
 * Compute boundary of multiple widgets
 *
 * @param widgetIds
 * @param widgets
 * @param zoomLevel
 * @returns
 */
const getMultipleWidgetBoundary = (widgetIds: WidgetId[], widgets: WidgetMap, zoomLevel: number) => {
  const multiWidgetBoundary = {
    top: Infinity,
    bottom: 0,
    left: Infinity,
    right: 0,
  };

  for (const widgetId of widgetIds) {
    const {
      top: widgetTop,
      left: widgetLeft,
      bottom: widgetBottom,
      right: widgetRight,
    } = getWidgetBoundary(widgetId, widgets, zoomLevel);
    const {
      top: groupWidgetTop,
      left: groupWidgetLeft,
      bottom: groupWidgetBottom,
      right: groupWidgetRight,
    } = multiWidgetBoundary;

    if (widgetTop < groupWidgetTop) multiWidgetBoundary.top = widgetTop;
    if (widgetLeft < groupWidgetLeft) multiWidgetBoundary.left = widgetLeft;
    if (widgetBottom > groupWidgetBottom) multiWidgetBoundary.bottom = widgetBottom;
    if (widgetRight > groupWidgetRight) multiWidgetBoundary.right = widgetRight;
  }

  return multiWidgetBoundary;
};

// Get actual widget boundary relative to the page
// Considers rotated widgets + responsive widgets
const getWidgetBoundary = (widgetId: WidgetId, widgets: WidgetMap, zoomLevel: number) => {
  const widget = widgets[widgetId];
  if (getWidgetTypeFromId(widgetId) === WidgetType.Group) {
    const memberWidgetIds = (widget as GroupWidgetData).memberWidgetIds;
    return getMultipleWidgetBoundary(memberWidgetIds, widgets, zoomLevel);
  }

  const { topPx, leftPx, widthPx, heightPx } = widget;
  let widgetBoundary = {
    top: topPx,
    left: leftPx,
    bottom: topPx + heightPx,
    right: leftPx + widthPx,
  };

  const isResponsiveWidget = widget.hasOwnProperty('memberWidgetIds');

  // No need to do further calcuations if the widget hasn't been rotated
  if (widget.rotateDeg === 0 && !isResponsiveWidget) return widgetBoundary;

  const widgetBB = document.getElementById(widgetId)?.getBoundingClientRect();
  if (!widgetBB) return widgetBoundary;

  widgetBB.width = widgetBB.width / zoomLevel;
  widgetBB.height = widgetBB.height / zoomLevel;

  if (isResponsiveWidget) {
    widgetBoundary = {
      top: topPx,
      left: leftPx,
      bottom: topPx + widgetBB.height,
      right: leftPx + widgetBB.width,
    };
  } else {
    // Rotated widget
    // Offset is the difference between the widget's actual size and the size of the bounding box
    const offset = { top: (heightPx - widgetBB.height) / 2, left: (widthPx - widgetBB.width) / 2 };

    widgetBoundary = {
      top: topPx + offset.top,
      left: leftPx + offset.left,
      bottom: topPx + heightPx - offset.top,
      right: leftPx + widthPx - offset.left,
    };
  }

  return widgetBoundary;
};

/**
 * Compute alignment boundary of widgets
 * Single widgets align to page
 * Multiple widgets align to the boundary of the widgets
 *
 * @param activeWidgets
 * @returns
 */
const computeAlignmentBoundary = (activeWidgets: ActiveWidget[]): Boundary => {
  const storeState = store.getState() as RootState;
  const zoomLevel = selectZoom(storeState);
  const pageWidth = selectInfographWidthPx(storeState);
  const pageHeight = selectInfographHeightPx(storeState);

  // Single widget case - should align to page boundary
  const alignToPageBoundary = activeWidgets.length === 1;

  if (alignToPageBoundary) {
    return {
      top: 0,
      left: 0,
      bottom: pageHeight,
      right: pageWidth,
    };
  }

  return getMultipleWidgetBoundary(
    activeWidgets.map((widget) => widget.groupId || widget.responsiveGroupId || widget.id),
    selectWidgets(storeState),
    zoomLevel,
  );
};

export { computeAlignmentBoundary, getWidgetBoundary, getMultipleWidgetBoundary };
